/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { collection, doc, getDoc, getDocs, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import useAdminClaim from "../hooks/useAdminClaim";

function getProfileSpeciesCount(profileData) {
  const count = Number(
    profileData?.speciesCount ?? profileData?.rankingSpeciesCount ?? 0,
  );

  return Number.isFinite(count) && count >= 0 ? Math.floor(count) : 0;
}

function formatSpeciesCount(value) {
  return `${(Number(value) || 0).toLocaleString()} species`;
}

const collectionSwitcherCache = new Map();
const collectionSwitcherRequests = new Map();

async function fetchCollectionOwnerData(currentUserUID, collectionOwnerUID) {
  const existingRequest = collectionSwitcherRequests.get(currentUserUID);
  if (existingRequest) return existingRequest;

  const request = (async () => {
    const friendsSnapshot = await getDocs(
      collection(db, "users", currentUserUID, "friends"),
    );

    const friendUIDs = friendsSnapshot.docs.map(
      (friendDocument) => friendDocument.id,
    );

    const profileUIDs = Array.from(
      new Set(
        [currentUserUID, collectionOwnerUID, ...friendUIDs].filter(Boolean),
      ),
    );

    const profileEntries = await Promise.all(
      profileUIDs.map(async (profileUID) => {
        try {
          const profileSnapshot = await getDoc(doc(db, "profile", profileUID));

          return [
            profileUID,
            profileSnapshot.exists() ? profileSnapshot.data() || {} : {},
          ];
        } catch (error) {
          console.error("Failed to load collection owner profile:", error);
          return [profileUID, {}];
        }
      }),
    );

    const profilesByUID = Object.fromEntries(profileEntries);
    const ownProfile = profilesByUID[currentUserUID] || {};

    const collectionOwners = [
      {
        uid: currentUserUID,
        name: String(ownProfile.name || "My collection").trim(),
        photoURL: String(ownProfile.photoURL || "").trim(),
        speciesCount: getProfileSpeciesCount(ownProfile),
        isSelf: true,
      },
      ...friendUIDs
        .map((friendUID) => {
          const friendProfile = profilesByUID[friendUID] || {};

          return {
            uid: friendUID,
            name:
              String(friendProfile.name || "BioIndex friend").trim() ||
              "BioIndex friend",
            photoURL: String(friendProfile.photoURL || "").trim(),
            speciesCount: getProfileSpeciesCount(friendProfile),
            isSelf: false,
          };
        })
        .sort(
          (a, b) =>
            b.speciesCount - a.speciesCount || a.name.localeCompare(b.name),
        ),
    ];

    const result = {
      collectionOwners,
      profilesByUID,
      loadedAt: Date.now(),
    };

    collectionSwitcherCache.set(currentUserUID, result);
    return result;
  })().finally(() => {
    collectionSwitcherRequests.delete(currentUserUID);
  });

  collectionSwitcherRequests.set(currentUserUID, request);
  return request;
}

export default function TaxonomyCollectionSwitcher({ uid, variant = "inline", onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin, adminLoading } = useAdminClaim();
  const switcherRef = useRef(null);
  const buttonRef = useRef(null);

  const initialCurrentUserUID = auth.currentUser?.uid || "";
  const exploreMatch = location.pathname.match(/^\/explore\/([^/]+)/);
  const routeUID = exploreMatch ? exploreMatch[1] : null;
  const initialCollectionOwnerUID = uid || routeUID || initialCurrentUserUID;
  const initialCache = collectionSwitcherCache.get(initialCurrentUserUID);

  const [currentUserUID, setCurrentUserUID] = useState(
    initialCurrentUserUID,
  );
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [collectionOwners, setCollectionOwners] = useState(
    () => initialCache?.collectionOwners || [],
  );
  const [currentOwnerProfile, setCurrentOwnerProfile] = useState(
    () => initialCache?.profilesByUID?.[initialCollectionOwnerUID] || null,
  );
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [ownersError, setOwnersError] = useState("");
  const [menuPosition, setMenuPosition] = useState(null);

  const collectionOwnerUID = uid || routeUID || currentUserUID;

  const adminModeActive =
    new URLSearchParams(location.search).get("adminMode") === "true";

  const canSwitchAdminMode =
    !adminLoading &&
    isAdmin &&
    currentUserUID &&
    collectionOwnerUID &&
    collectionOwnerUID !== currentUserUID;

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      setCurrentUserUID(user?.uid || "");
    });
  }, []);

  useEffect(() => {
    setSwitcherOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!switcherOpen) return undefined;

    function closeSwitcher(event) {
      if (!switcherRef.current?.contains(event.target)) {
        setSwitcherOpen(false);
      }
    }

    document.addEventListener("mousedown", closeSwitcher);
    document.addEventListener("touchstart", closeSwitcher, { passive: true });

    return () => {
      document.removeEventListener("mousedown", closeSwitcher);
      document.removeEventListener("touchstart", closeSwitcher);
    };
  }, [switcherOpen]);

  useEffect(() => {
    if (!switcherOpen) return undefined;

    function positionMenu() {
      const buttonBounds = buttonRef.current?.getBoundingClientRect();

      if (!buttonBounds) return;

      const viewportWidth = window.innerWidth;
      const menuWidth = Math.min(340, viewportWidth - 24);
      const centeredLeft =
        buttonBounds.left + buttonBounds.width / 2 - menuWidth / 2;
      const left = Math.max(
        12,
        Math.min(centeredLeft, viewportWidth - menuWidth - 12),
      );

      setMenuPosition({
        left,
        top: buttonBounds.bottom + 8,
        width: menuWidth,
      });
    }

    positionMenu();
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);

    return () => {
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [switcherOpen]);

  useEffect(() => {
    let cancelled = false;

    if (!currentUserUID) {
      setCollectionOwners([]);
      setCurrentOwnerProfile(null);
      setOwnersLoading(false);
      setOwnersError("");
      return undefined;
    }

    async function loadCollectionOwners() {
      const cached = collectionSwitcherCache.get(currentUserUID);
      const cachedOwnerProfile = cached?.profilesByUID?.[collectionOwnerUID];

      if (cached) {
        setCollectionOwners(cached.collectionOwners);
        setCurrentOwnerProfile(cachedOwnerProfile || null);
      }

      // Cached counts are only a display placeholder. Refresh on navigation:
      // taxonomic revisions can change an owner's species total mid-session.

      setOwnersLoading(!cached);
      setOwnersError("");

      try {
        const result = await fetchCollectionOwnerData(
          currentUserUID,
          collectionOwnerUID,
        );

        if (cancelled) return;

        setCollectionOwners(result.collectionOwners);
        setCurrentOwnerProfile(result.profilesByUID[collectionOwnerUID] || null);
      } catch (error) {
        console.error("Failed to load friend collections:", error);

        if (!cancelled && !cached) {
          setOwnersError("Friend collections could not be loaded.");
          setCollectionOwners([
            {
              uid: currentUserUID,
              name: "My collection",
              photoURL: "",
              speciesCount: 0,
              isSelf: true,
            },
          ]);
        }
      } finally {
        if (!cancelled) setOwnersLoading(false);
      }
    }

    loadCollectionOwners();

    return () => {
      cancelled = true;
    };
  }, [collectionOwnerUID, currentUserUID]);

  // The currently viewed collection can change because of a taxonomy
  // revision. Prefer the authoritative profile count over a cached switcher.
  useEffect(() => {
    if (!collectionOwnerUID) return undefined;
    return onSnapshot(doc(db, "profile", collectionOwnerUID), (snapshot) => {
      if (!snapshot.exists()) return;
      const updated = snapshot.data();
      setCurrentOwnerProfile(updated);
      setCollectionOwners((previous) => previous.map((owner) =>
        owner.uid === collectionOwnerUID
          ? { ...owner, speciesCount: getProfileSpeciesCount(updated) }
          : owner));
    }, () => {});
  }, [collectionOwnerUID]);

  const currentCollectionOwner = useMemo(
    () =>
      collectionOwners.find((owner) => owner.uid === collectionOwnerUID) ||
      null,
    [collectionOwnerUID, collectionOwners],
  );

  const currentCollectionIsOwn = collectionOwnerUID === currentUserUID;
  const currentCollectionName = currentCollectionIsOwn
    ? "My collection"
    : `${String(
        currentCollectionOwner?.name || currentOwnerProfile?.name || "Friend",
      ).trim()}'s collection`;

  const currentCollectionSpeciesCount =
    currentCollectionOwner?.speciesCount ??
    getProfileSpeciesCount(currentOwnerProfile);

  const mobileOwnerLabel = currentCollectionIsOwn
    ? "Mine"
    : String(
        currentCollectionOwner?.name || currentOwnerProfile?.name || "Friend",
      )
        .trim()
        .split(/\s+/)[0];

  function switchCollection(targetUID) {
    if (!targetUID) return;

    setSwitcherOpen(false);
    onClose?.();
    navigate(
      targetUID === currentUserUID ? "/explore" : `/explore/${targetUID}`,
    );
  }

  function toggleAdminMode() {
    if (!canSwitchAdminMode) return;

    const nextSearchParams = new URLSearchParams(location.search);

    if (adminModeActive) {
      nextSearchParams.delete("adminMode");
    } else {
      nextSearchParams.set("adminMode", "true");
    }

    const nextSearch = nextSearchParams.toString();

    setSwitcherOpen(false);

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }

  if (variant === "dialog") {
    return (
      <div
        className="fixed inset-0 z-[140] flex items-center justify-center bg-[#26343B]/45 px-5 py-8"
        role="presentation"
        onClick={onClose}
      >
        <section
          role="dialog"
          aria-modal="true"
          aria-label="Switch collection"
          onClick={(event) => event.stopPropagation()}
          className="w-full max-w-[350px] overflow-hidden rounded-2xl border border-[#D7E8DB] bg-white text-[#374049] shadow-[0_20px_56px_rgba(17,40,42,.27)]"
        >
          <header className="flex items-center justify-between gap-3 border-b border-[#E2ECE5] px-4 py-3">
            <div className="min-w-0">
              <h2 className="text-sm font-bold">Switch collection</h2>
              <p className="mt-0.5 text-[10px] text-[#748187]">Choose whose BioIndex to explore</p>
            </div>
            <button type="button" onClick={onClose} className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[#F4F8F4] text-lg text-[#52626A]" aria-label="Close collection picker">×</button>
          </header>
          <div className="max-h-[min(62dvh,390px)] space-y-1 overflow-y-auto overscroll-contain px-2 py-2">
            {ownersLoading && <p className="px-3 py-4 text-center text-xs text-[#72818A]">Loading collections…</p>}
            {!ownersLoading && collectionOwners.map((owner) => {
              const selected = owner.uid === collectionOwnerUID;
              return (
                <button
                  key={owner.uid}
                  type="button"
                  onClick={() => switchCollection(owner.uid)}
                  aria-current={selected ? "true" : undefined}
                  className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition ${selected ? "bg-[#E8F7F2] text-[#01767A]" : "hover:bg-[#F4F8F4]"}`}
                >
                  {owner.photoURL ? (
                    <img src={owner.photoURL} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
                  ) : (
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#374049] text-xs font-semibold text-white">{owner.isSelf ? "Me" : owner.name.charAt(0).toUpperCase()}</span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-semibold">{owner.isSelf ? "My collection" : owner.name}</span>
                    <span className="text-[10px] text-[#748187]">{formatSpeciesCount(owner.speciesCount)}</span>
                  </span>
                  {selected && <span className="shrink-0 text-[10px] font-bold text-[#01767A]">Current</span>}
                </button>
              );
            })}
            {!ownersLoading && collectionOwners.length === 1 && <p className="px-3 py-2 text-center text-[11px] text-[#748187]">Add friends to explore other collections.</p>}
            {ownersError && <p className="px-3 py-2 text-xs text-amber-700">{ownersError}</p>}
          </div>
          {canSwitchAdminMode && (
            <div className="border-t border-[#E2ECE5] p-2">
              <button type="button" onClick={() => { onClose?.(); toggleAdminMode(); }} className="w-full rounded-lg bg-[#F2F7F2] px-3 py-2 text-left text-xs font-semibold text-[#374049]">
                {adminModeActive ? "Exit Admin Mode" : "Enter Admin Mode"}
              </button>
            </div>
          )}
        </section>
      </div>
    );
  }

  return (
    <div
      ref={switcherRef}
      className="relative flex min-w-0 flex-1 sm:flex-none"
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          if (currentUserUID) setSwitcherOpen((currentValue) => !currentValue);
        }}
        disabled={!currentUserUID}
        aria-expanded={switcherOpen}
        aria-haspopup="listbox"
        aria-current="page"
        aria-label={`Switch collection. Currently exploring ${currentCollectionName}`}
        className="flex w-full min-w-0 flex-col items-center justify-center rounded-xl bg-[#24C3BE]/15 px-1 py-1 text-[#374049] shadow-inner transition hover:bg-[#24C3BE]/25 focus:outline-none focus:ring-4 focus:ring-[#24C3BE]/20 disabled:cursor-default sm:w-auto sm:min-w-[215px] sm:flex-row sm:gap-2.5 sm:bg-[#24C3BE] sm:px-3 sm:py-1.5 sm:text-white sm:hover:bg-[#01767A]"
      >
        <img
          src="/ExploreIcon.png"
          alt=""
          aria-hidden="true"
          className="h-7 w-7 shrink-0 object-contain sm:hidden"
        />

        <span className="hidden h-7 w-7 shrink-0 sm:flex">
          {currentCollectionOwner?.photoURL || currentOwnerProfile?.photoURL ? (
            <img
              src={
                currentCollectionOwner?.photoURL ||
                currentOwnerProfile?.photoURL
              }
              alt=""
              className="h-7 w-7 rounded-full border border-white/60 object-cover"
            />
          ) : (
            <span className="flex h-7 w-7 items-center justify-center rounded-full border border-white/60 bg-[#374049] text-[9px] font-semibold text-white">
              {currentCollectionIsOwn
                ? "Me"
                : currentCollectionName.charAt(0).toUpperCase() || "B"}
            </span>
          )}
        </span>

        <span className="flex max-w-full items-center gap-0.5 text-[9px] font-semibold leading-none sm:hidden">
          <span className="max-w-[3.75rem] truncate">{mobileOwnerLabel}</span>
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
            className={`h-3 w-3 shrink-0 transition-transform ${
              switcherOpen ? "rotate-180" : ""
            }`}
          >
            <path
              d="m5.5 7.5 4.5 4.5 4.5-4.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>

        <span className="hidden min-w-0 flex-1 text-left sm:block">
          <span className="block text-[9px] font-semibold uppercase leading-none tracking-[0.12em] text-white/75">
            Exploring
          </span>
          <span className="mt-0.5 flex min-w-0 items-baseline gap-1.5 leading-none">
            <span className="max-w-[8.5rem] truncate text-sm font-semibold">
              {currentCollectionName}
            </span>
            <span className="shrink-0 text-[10px] font-medium text-white/75">
              {formatSpeciesCount(currentCollectionSpeciesCount)}
            </span>
          </span>
        </span>

        <svg
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
          className={`hidden h-4 w-4 shrink-0 transition-transform sm:block ${
            switcherOpen ? "rotate-180" : ""
          }`}
        >
          <path
            d="m5.5 7.5 4.5 4.5 4.5-4.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {switcherOpen && menuPosition && (
        <div
          className="fixed z-[100] overflow-hidden rounded-2xl border border-[#93CA87]/45 bg-white text-[#374049] shadow-2xl"
          style={menuPosition}
        >
          <div className="border-b border-[#93CA87]/30 bg-[#FCFFF6] px-4 py-3">
            <div className="text-xs font-semibold">Switch collection</div>
            <div className="mt-0.5 text-[10px] text-[#374049]/55">
              Opens at the Kingdom level
            </div>
          </div>

          <div
            role="listbox"
            aria-label="Choose a BioIndex collection"
            className="max-h-[calc(100vh-9rem)] overflow-y-auto overscroll-contain p-2 sm:max-h-72"
          >
            {ownersLoading ? (
              <div className="flex items-center justify-center gap-2 px-3 py-6 text-xs text-[#374049]/55">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#93CA87]/45 border-t-[#24C3BE]" />
                Loading collections...
              </div>
            ) : (
              collectionOwners.map((owner) => {
                const isCurrentOwner = owner.uid === collectionOwnerUID;
                const ownerLabel = owner.isSelf ? "My collection" : owner.name;

                return (
                  <button
                    type="button"
                    role="option"
                    aria-selected={isCurrentOwner}
                    key={owner.uid}
                    onClick={() => switchCollection(owner.uid)}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition ${
                      isCurrentOwner
                        ? "bg-[#24C3BE]/12 text-[#01767A]"
                        : "hover:bg-[#FCFFF6]"
                    }`}
                  >
                    {owner.photoURL ? (
                      <img
                        src={owner.photoURL}
                        alt=""
                        className="h-9 w-9 shrink-0 rounded-full border border-[#93CA87]/45 object-cover"
                      />
                    ) : (
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#374049] text-xs font-semibold text-white">
                        {owner.isSelf
                          ? "Me"
                          : owner.name.charAt(0).toUpperCase() || "B"}
                      </span>
                    )}

                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">
                        {ownerLabel}
                      </span>
                      <span className="block text-[10px] text-[#374049]/50">
                        {formatSpeciesCount(owner.speciesCount)}
                      </span>
                    </span>

                    {isCurrentOwner && (
                      <span className="shrink-0 rounded-full bg-[#24C3BE] px-2 py-1 text-[9px] font-semibold text-white">
                        Current
                      </span>
                    )}
                  </button>
                );
              })
            )}

            {!ownersLoading && collectionOwners.length === 1 && (
              <div className="px-3 py-4 text-center text-xs text-[#374049]/50">
                Add friends to explore their collections here.
              </div>
            )}

            {ownersError && (
              <div className="mt-1 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[10px] text-amber-700">
                {ownersError}
              </div>
            )}
          </div>

          {canSwitchAdminMode && (
            <div className="border-t border-[#93CA87]/30 bg-[#FCFFF6] p-2">
              <button
                type="button"
                onClick={toggleAdminMode}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition ${
                  adminModeActive
                    ? "border border-[#374049]/25 bg-white text-[#374049] hover:bg-[#374049]/5"
                    : "bg-[#374049] text-white hover:bg-[#01767A]"
                }`}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-current/30">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    className="h-4 w-4"
                  >
                    <path d="M12 3 19 6v5c0 4.8-2.8 8.2-7 10-4.2-1.8-7-5.2-7-10V6l7-3Z" />
                  </svg>
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold">
                    {adminModeActive ? "Exit Admin Mode" : "Enter Admin Mode"}
                  </span>

                  <span
                    className={`block text-[10px] ${
                      adminModeActive ? "text-[#374049]/55" : "text-white/65"
                    }`}
                  >
                    Administrative observation controls
                  </span>
                </span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
